<template>
  <div class="land-page">
    <v-container fluid class="land-container">
      <div class="breadcrumb-line">{{ $t("reports.reportsBreadcrumb") }} <span>›</span> {{ $t("reports.landSuitability") }}</div>

      <div class="page-heading">
        <div class="title-wrap">
          <h1>{{ $t("reports.landSuitability") }}</h1>
          <!-- <button class="guide-button" type="button">
            <v-icon size="15" color="white">mdi-help-circle-outline</v-icon>
            {{ $t("reports.aiReportGuide") }}
          </button> -->
        </div>
        <div class="heading-actions">
          <v-btn outlined color="primary" class="small-action" @click="goBack">{{ $t("reports.cancel") }}</v-btn>
          <v-btn
            color="primary"
            class="primary-action"
            :loading="generating"
            :disabled="!canGenerate"
            @click="generateReport"
          >
            {{ $t('reports.generateReport') }}
          </v-btn>
        </div>
      </div>

      <section class="create-card">
        <div class="form-intro">
          <div>
            <h2>{{ $t("reports.selectFarmGeofence") }}</h2>
            <p>
              {{ $t("reports.selectFarmGeofenceDescription") }}
            </p>
          </div>
        </div>

        <div class="create-grid">
          <div class="map-panel">
            <geofence-map
              :source="source"
              :zone="selectedZone"
              :geojson-coordinates="geojsonCoords"
              :radius-meters="circleRadius"
              @update:geometry="onDrawGeometry"
            />
          </div>

          <div class="selection-panel">
            <v-btn-toggle v-model="source" mandatory dense class="flex-wrap mb-4 d-flex source-toggle">
              <v-btn value="farm_zone">{{ $t("reports.sourceFarmZone") }}</v-btn>
              <v-btn value="polygon">{{ $t("reports.sourcePolygon") }}</v-btn>
              <v-btn value="circle">{{ $t("reports.sourceCircle") }}</v-btn>
              <v-btn value="geojson">{{ $t("reports.sourceGeojson") }}</v-btn>
            </v-btn-toggle>

            <!-- Farm / Zone -->
            <div v-if="source === 'farm_zone'" class="field-block">
              <label>{{ $t("reports.selectFarmZone") }}</label>
              <farm-zone-select
                v-model="selectedZone"
                :label="$t('reports.selectFarmZone')"
              />
            </div>

            <!-- Draw polygon: instructions -->
            <div v-else-if="source === 'polygon'" class="field-block">
              <v-alert dense text type="info" class="mb-0">
                {{ $t("reports.drawPolygon") }} — {{ $t("reports.drawPolygonHelp") }}
              </v-alert>
            </div>

            <!-- Draw circle: instructions + radius -->
            <div v-else-if="source === 'circle'" class="field-block">
              <v-alert dense text type="info" class="mb-3">
                {{ $t("reports.drawCircle") }} — {{ $t("reports.drawCircleHelp") }}
              </v-alert>
              <label>{{ $t("reports.radiusM") }}</label>
              <v-text-field
                v-model.number="circleRadius"
                type="number"
                min="1"
                suffix="m"
                outlined
                dense
                hide-details
              />
            </div>

            <!-- Upload GeoJSON -->
            <div v-else-if="source === 'geojson'" class="upload-block">
              <h3>{{ $t("reports.uploadGeojsonFile") }}</h3>
              <p>
                {{ $t("reports.uploadGeojsonFileDescription") }}
              </p>
              <v-file-input
                accept=".geojson,.json"
                :label="$t('reports.selectAFile')"
                prepend-icon="mdi-upload"
                outlined
                dense
                hide-details
                @change="onGeojsonUpload"
              />
            </div>

            <div class="field-block crop-field">
              <label>{{ $t("reports.selectCrop") }}</label>
              <v-autocomplete
                v-model="cropId"
                :items="crops"
                item-text="name"
                item-value="id"
                :placeholder="$t('reports.select')"
                outlined
                dense
                hide-details
                :loading="loadingCrops"
              />
            </div>
          </div>
        </div>

        <estimated-compute-cost
          report-type="land_suitability"
          :geofence="geofencePayload"
        />
      </section>
    </v-container>
  </div>
</template>

<script>
import LandSuitabilityAndWeatherService from "@/_services/LandSuitabilityAndWeatherService";
import FarmZoneSelect from "../crop-progression/components/FarmZoneSelect.vue";
import GeofenceMap from "../crop-progression/components/GeofenceMap.vue";
import EstimatedComputeCost from "../shared/EstimatedComputeCost.vue";
import { circleToPolygon, centroidOf } from "@/utils/geoShapes";

const DEFAULT_CIRCLE_RADIUS_M = 20; // meters

export default {
  name: "LandSuitabilityReport",
  components: { FarmZoneSelect, GeofenceMap, EstimatedComputeCost },
  data() {
    return {
      crops: [],
      selectedZone: null,
      cropId: null,
      source: "farm_zone", // 'farm_zone' | 'polygon' | 'circle' | 'geojson'
      circleRadius: DEFAULT_CIRCLE_RADIUS_M,
      drawnGeometry: null,
      geojsonCoords: [],
      loadingCrops: false,
      generating: false,
    };
  },
  computed: {
    // Unified coordinate ring for whichever source is active, used both to
    // build the API payload and to know whether a valid geometry exists.
    zoneRing() {
      if (!this.selectedZone) return [];
      const rawCoordinates =
        this.selectedZone.geofence_coordinates || this.selectedZone.coordinates || [];
      const path = (Array.isArray(rawCoordinates) ? rawCoordinates : [])
        .map((point) => ({
          lat: Number(point.lat ?? point.latitude),
          lng: Number(point.lng ?? point.log ?? point.longitude),
        }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
      if (path.length >= 3) return path;

      const lat = Number(this.selectedZone.geofenceCenterLat ?? this.selectedZone.lat ?? this.selectedZone.latitude);
      const lng = Number(
        this.selectedZone.geofenceCenterLog ?? this.selectedZone.lng ?? this.selectedZone.log ?? this.selectedZone.longitude
      );
      return Number.isFinite(lat) && Number.isFinite(lng) ? [{ lat, lng }] : [];
    },
    ring() {
      switch (this.source) {
        case "farm_zone":
          return this.zoneRing;
        case "polygon":
          return this.drawnGeometry && this.drawnGeometry.shape === "polygon"
            ? this.drawnGeometry.coordinates
            : [];
        case "circle":
          return this.drawnGeometry && this.drawnGeometry.shape === "circle"
            ? circleToPolygon(
                this.drawnGeometry.latitude,
                this.drawnGeometry.longitude,
                this.drawnGeometry.radius * 1000
              )
            : [];
        case "geojson":
          return this.geojsonCoords;
        default:
          return [];
      }
    },
    hasGeometry() {
      return this.ring.length > 0;
    },
    geofencePayload() {
      switch (this.source) {
        case "farm_zone":
          return this.selectedZone
            ? {
                geofenceType: "farm_zone",
                geofenceId: this.selectedZone.geofenceId,
                farmId: this.selectedZone.farmId,
              }
            : null;
        case "polygon":
          return this.drawnGeometry && this.drawnGeometry.shape === "polygon"
            ? { geofenceType: "polygon", coordinates: this.drawnGeometry.coordinates }
            : null;
        case "circle":
          return this.drawnGeometry && this.drawnGeometry.shape === "circle"
            ? {
                geofenceType: "circle",
                latitude: this.drawnGeometry.latitude,
                longitude: this.drawnGeometry.longitude,
                radius: this.drawnGeometry.radius,
              }
            : null;
        case "geojson":
          return this.geojsonCoords.length >= 3
            ? { geofenceType: "geojson", coordinates: this.geojsonCoords }
            : null;
        default:
          return null;
      }
    },
    canGenerate() {
      return Boolean(this.cropId && this.hasGeometry) && !this.generating;
    },
  },
  watch: {
    source() {
      this.drawnGeometry = null;
      if (this.source !== "geojson") this.geojsonCoords = [];
      if (this.source !== "farm_zone") this.selectedZone = null;
      if (this.source === "circle") this.circleRadius = DEFAULT_CIRCLE_RADIUS_M;
    },
  },
  async mounted() {
    await this.loadCrops();
  },
  methods: {
    goBack() {
      this.$router.push({ name: "landSuitabilityDesignList" });
    },
    onDrawGeometry(geom) {
      this.drawnGeometry = geom;
      if (geom && geom.shape === "circle" && geom.radius != null) {
        const meters = Math.round(geom.radius * 1000);
        if (Math.abs(meters - Number(this.circleRadius)) >= 1) {
          this.circleRadius = meters;
        }
      }
    },
    async loadCrops() {
      this.loadingCrops = true;
      try {
        const response = await LandSuitabilityAndWeatherService.getLandSuitabilityCrops();
        const crops = (response.data && response.data.crops) || response.data || [];
        this.crops = (Array.isArray(crops) ? crops : []).map((crop) => ({
          ...crop,
          id: crop.id || crop.crop_id,
          name: crop.name || crop.crop_name || crop.cropName || `Crop ${crop.id || crop.crop_id}`,
        }));
      } catch (error) {
        this.crops = [];
        this.$notify({
          title: this.$t("reports.unableToLoadAvailableCrops"),
          type: "error",
        });
      } finally {
        this.loadingCrops = false;
      }
    },
    async onGeojsonUpload(file) {
      if (!file) {
        this.geojsonCoords = [];
        return;
      }
      const text = await file.text().catch(() => null);
      if (!text) return;
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (error) {
        this.$notify({
          title: this.$t("reports.invalidGeojsonFile"),
          text: this.$t("reports.invalidGeojsonFileDescription"),
          type: "error",
        });
        return;
      }
      const coords = this.extractPolygonFromGeojson(parsed);
      if (coords.length < 3) {
        this.$notify({
          title: this.$t("reports.invalidGeojsonFile"),
          text: this.$t("reports.invalidGeojsonFileDescription"),
          type: "error",
        });
        return;
      }
      this.geojsonCoords = coords;
    },
    extractPolygonFromGeojson(geojson) {
      const ringToLatLng = (ring) => ring.map((c) => ({ lat: c[1], lng: c[0] }));
      try {
        if (geojson.type === "FeatureCollection" && geojson.features && geojson.features.length) {
          return this.extractPolygonFromGeojson(geojson.features[0]);
        }
        const geom = geojson.type === "Feature" ? geojson.geometry : geojson;
        if (!geom) return [];
        if (geom.type === "Polygon") return ringToLatLng(geom.coordinates[0]);
        if (geom.type === "MultiPolygon") return ringToLatLng(geom.coordinates[0][0]);
      } catch (error) {
        return [];
      }
      return [];
    },
    async generateReport() {
      this.generating = true;
      try {
        const centroid = centroidOf(this.ring) || {};
        const response = await LandSuitabilityAndWeatherService.generateLandSuitabilityReport({
          farmId: this.selectedZone ? this.selectedZone.farmId : null,
          geofenceId: this.selectedZone ? this.selectedZone.geofenceId : null,
          zoneName: this.selectedZone ? (this.selectedZone.zoneName || null) : null,
          cropId: this.cropId,
          latitude: centroid.lat,
          longitude: centroid.lng,
          geometryType: this.ring.length >= 3 ? "polygon" : "point",
          geofenceCoordinates: this.ring.map((point) => ({
            lat: Number(point.lat),
            lng: Number(point.lng),
          })),
        });
        if (!response.success) throw new Error(response.message || this.$t("reports.unableToGenerateReport"));
        await this.$router.replace({
          name: "landSuitabilityDesignView",
          params: { id: response.data.id },
        });
      } catch (error) {
        const apiMessage =
          (error && error.data && (error.data.message || error.data.msg)) ||
          (error && error.message) ||
          this.$t("reports.unableToGenerateReport");
        this.$notify({
          title: apiMessage,
          type: "error",
        });
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.land-page {
  min-height: 100%;
  background: #f5f7fa;
  color: #20262d;
}

.land-container {
  padding: 28px 32px 42px;
}

.breadcrumb-line {
  margin-bottom: 12px;
  color: #7c858d;
  font-size: 12px;

  span {
    margin: 0 4px;
  }
}

.page-heading,
.title-wrap,
.heading-actions,
.form-intro {
  display: flex;
  align-items: center;
}

.page-heading {
  justify-content: space-between;
  margin-bottom: 20px;
}

.title-wrap {
  gap: 14px;

  h1 {
    margin: 0;
    font-size: 27px;
    line-height: 1.2;
  }
}

.guide-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 30px;
  padding: 0 13px;
  border-radius: 16px;
  background: #18b879;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.heading-actions {
  gap: 12px;

  .v-btn {
    height: 40px;
    border-radius: 3px;
    box-shadow: none;
    font-size: 12px;
    font-weight: 700;
  }

  .small-action {
    min-width: 100px;
  }

  .primary-action,
  .delete-action {
    min-width: 150px;
  }
}

.create-card {
  padding: 22px 24px 28px;
  background: #fff;
}

.form-intro {
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;

  h2 {
    margin: 0 0 4px;
    font-size: 17px;
  }

  p {
    max-width: 660px;
    margin: 0;
    color: #56616a;
    font-size: 12px;
  }
}

.create-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(310px, 0.95fr);
  gap: 24px;
}

.map-panel {
  position: relative;
}

.selection-panel {
  padding-top: 4px;
}

.source-toggle {
  width: 100%;

  ::v-deep .v-btn {
    flex: 1;
  }
}

.field-block {
  margin-bottom: 18px;

  label {
    display: block;
    margin-bottom: 7px;
    color: #25313a;
    font-size: 12px;
    font-weight: 600;
  }

  ::v-deep .v-input__slot {
    min-height: 40px !important;
    border-radius: 2px;
  }
}

.upload-block {
  padding: 0 0 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #dce1e4;

  h3 {
    margin: 0 0 8px;
    color: #18a56d;
    font-size: 13px;
  }

  p {
    margin-bottom: 12px;
    color: #4f5961;
    font-size: 11px;
    line-height: 1.45;
  }
}

.crop-field {
  margin-top: 16px;
}

@media (max-width: 1000px) {
  .page-heading,
  .form-intro {
    align-items: flex-start;
    gap: 16px;
    flex-direction: column;
  }

  .create-grid {
    grid-template-columns: 1fr;
  }

}
</style>
